// ─── the face host ───────────────────────────────────────────────────────────
// Owns the mounted face and nothing else. The app never learns which face it got,
// and the face never learns there is a protocol: it receives `FaceState` and
// `MouthFrame`s, which is the whole of `face/FACE_API.md`.
//
// Swapping is a runtime act, not a reload — the session, the socket and the audio
// graph all outlive it, so `use()` disposes the old face, mounts the new one and
// re-applies the state (and the user's model) it was already showing.

import {
  DEFAULT_FACE_ID,
  acceptsModel,
  createFace,
  isFaceId,
  type Face,
  type FaceId,
  type FaceState,
  type MouthFrame,
} from '@voicebox/face'

const ACCENT = '#5ee7ff'

export class FaceHost {
  private face: Face | null = null
  private state: FaceState = { mode: 'idle', emotion: 'neutral' }
  private model: Blob | string | null = null
  private swapping: Promise<void> = Promise.resolve()
  private observer: ResizeObserver | null = null
  id: FaceId = DEFAULT_FACE_ID

  constructor(
    private readonly container: HTMLElement,
    private readonly onNotice: (message: string) => void,
  ) {}

  /**
   * Mount `id`, replacing whatever is there. Swaps are serialised: clicking three
   * faces quickly must end on the third, not on whichever WebGL context won.
   */
  use(id: string): Promise<void> {
    const next = isFaceId(id) ? id : DEFAULT_FACE_ID
    this.swapping = this.swapping.then(() => this.swap(next)).catch(() => {})
    return this.swapping
  }

  private async swap(id: FaceId): Promise<void> {
    this.teardown()
    try {
      this.face = await createFace(id, { accent: ACCENT, background: 'transparent' })
      await this.face.mount(this.container)
      this.id = id
    } catch (err) {
      // `orb` and `vrm` need WebGL; `dom` is the floor of the system and always works.
      this.onNotice(`the ${id} face could not start (${err instanceof Error ? err.message : String(err)}) — falling back to dom`)
      if (id === 'dom') throw err
      return this.swap('dom')
    }
    this.face.setState(this.state)
    this.fit()
    this.observer = new ResizeObserver(() => this.fit())
    this.observer.observe(this.container)
    if (this.model && acceptsModel(this.face)) await this.applyModel(this.model)
  }

  setState(state: FaceState): void {
    this.state = state
    this.face?.setState(state)
  }

  get mode(): FaceState['mode'] {
    return this.state.mode
  }

  drive(frame: MouthFrame): void {
    this.face?.drive(frame)
  }

  /** True when the mounted face takes a user-supplied model (the VRM avatar). */
  get takesModel(): boolean {
    return !!this.face && acceptsModel(this.face)
  }

  get hasModel(): boolean {
    return !!this.face && acceptsModel(this.face) && this.face.hasModel
  }

  /** `source` is a picked file or a URL we are allowed to fetch. Never bundled. */
  async loadModel(source: Blob | string): Promise<void> {
    await this.swapping
    if (!this.face || !acceptsModel(this.face)) throw new Error(`the ${this.id} face does not take a model`)
    await this.applyModel(source)
    this.model = source
  }

  clearModel(): void {
    this.model = null
  }

  private async applyModel(source: Blob | string): Promise<void> {
    const face = this.face
    if (!face || !acceptsModel(face)) return
    await face.loadModel(source)
  }

  private fit(): void {
    const box = this.container.getBoundingClientRect()
    if (box.width && box.height) this.face?.resize(box.width, box.height)
  }

  private teardown(): void {
    this.observer?.disconnect()
    this.observer = null
    this.face?.dispose()
    this.face = null
    this.container.replaceChildren()
  }

  dispose(): void {
    this.teardown()
  }
}
